import { AvField, AvForm } from "availity-reactstrap-validation";
import { memo, useEffect, useState } from "react";
import { withNamespaces } from "react-i18next";
import { useHistory, withRouter, useLocation } from "react-router-dom/cjs/react-router-dom";
import { Button, Card, CardBody, Col, Label, Row, Spinner } from "reactstrap";
import { roles, routes } from "../../../../data";
import { useMutation } from "@tanstack/react-query";
import { createNewUser } from "../../../../api/apiFunctions";
import { toast } from "react-toastify";
import { connect } from "react-redux";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import axios from "axios";
import EntitySelection from "../../../../components/entity-selection";
import ValidatableEmail from "../../../../components/validatable-email";
import InputPhoneNumber from "src/components/Fields/InputPhoneNumber";

const AddNewUser = (props) => {
    const { t, token, modules } = props;
    const activeModule = modules?.active;
    const history = useHistory();
    const location = useLocation();

    const [items, setItems] = useState([
        {
            salutation: "",
            first_name: "",
            last_name: "",
            email: "",
            position: "",
            department: "",
            phone_number: "",
            mobile_number: "",
            role: "",
            entity_id: null,
            institute_id: null,
            division_id: null
        },
    ]);

    const renderSubmitErrorMessage = (val) => {
        let result = "";

        if (val?.email === "validation.email.already_used") {
            result = t("This email has already been used.");
        } else if (val === "insufficient_license_ROLE_ANALYST_ADMIN") {
            result = t("Insufficient licenses for role Analyst Admin.");
        } else if (val === "insufficient_license_ROLE_ANALYST") {
            result = t("Insufficient licenses for role Analyst.");
        } else if (val === "insufficient_license_ROLE_EMPLOYEE") {
            result = t("Insufficient licenses for role Employee.");
        } else if (val === "insufficient_license_ROLE_TASK_OWNER") {
            result = t("Insufficient licenses for role Task Owner.");
        } else {
            result = t(JSON.stringify(val));
        }

        return result;
    };

    const handleSubmitMutation = useMutation({
        mutationFn: async () => {
            const payload = items.map((i) => {
                const item = {
                    gender: i.salutation,
                    first_name: i.first_name,
                    last_name: i.last_name,
                    email: i.email,
                    position: i.position,
                    department: i.department,
                    phone_number: i.phone_number || "",
                    mobile_number: i.mobile_number || "",
                    role: i.role,
                    division: i.division_id,
                };
                return item;
            });

            await createNewUser({
                token: token,
                payload,
                activeModule,
            });
        },
        onSuccess: () => {
            toast("User created successfully.", {
                type: "success",
            });
            history.push(routes[activeModule].listOfUsers);
        },
        onError: (err) => {
            if (axios.isAxiosError(err)) {
                const error = err?.response?.data?.message;
                Object.values(error).forEach((val) => {
                    toast(renderSubmitErrorMessage(val), {
                        type: "error",
                    });
                });
            }
        }
    });

    const addUser = () => {
        setItems([
            ...items,
            {
                salutation: "",
                first_name: "",
                last_name: "",
                email: "",
                position: "",
                department: "",
                phone_number: "",
                mobile_number: "",
                role: "",
                entity_id: null,
                institute_id: null,
                division_id: null
            },
        ]);
    };

    const deleteUser = (index) => {
        if (items.length > 1) {
            setItems(
                items.filter((item, itemIndex) => {
                    return index !== itemIndex;
                })
            );
        }
    };

    const fieldValueChanged = (index, key, value) => {
        setItems((prevItems) =>
            prevItems.map((item, itemIndex) => {
                if (index === itemIndex) {
                    return {
                        ...item,
                        [key]: value,
                    };
                }
                return item;
            })
        );
    };

    useEffect(() => {
        const csvLoadedUsers = location.state?.csvLoadedUsers || [];
        if(csvLoadedUsers.length > 0) {
            const headersRow = csvLoadedUsers[0];
            const findHeaderIndex = (...names) => {
                return headersRow.findIndex((header) =>
                    names.includes(String(header || '').toLowerCase().trim())
                );
            };

            let salutationIndex = findHeaderIndex('salutation');
            let firstNameIndex = findHeaderIndex('first name', 'firstname');
            let lastNameIndex = findHeaderIndex('last name', 'lastname');
            let emailIndex = findHeaderIndex('email');
            let companyPositionIndex = findHeaderIndex('position', 'company position');
            let roleIndex = findHeaderIndex('role');
            let departmentIndex = findHeaderIndex('department', 'division');
            let phoneNumberIndex = findHeaderIndex('office number', 'phone number', 'phone');
            let mobileNumberIndex = findHeaderIndex('mobile number', 'mobile');

            if (salutationIndex < 0) salutationIndex = 0;
            if (firstNameIndex < 0) firstNameIndex = 1;
            if (lastNameIndex < 0) lastNameIndex = 2;
            if (emailIndex < 0) emailIndex = 3;
            if (companyPositionIndex < 0) companyPositionIndex = 4;
            if (roleIndex < 0) roleIndex = 5;
            if (departmentIndex < 0) departmentIndex = 6;
            if (phoneNumberIndex < 0) phoneNumberIndex = 7;
            if (mobileNumberIndex < 0) mobileNumberIndex = 8;

            const rolesMapping = {
                "Analyst Admin": "ROLE_ANALYST_ADMIN",
                "Analyst": "ROLE_ANALYST",
                "Employee": "ROLE_EMPLOYEE",
                "Task Owner": "ROLE_TASK_OWNER",
            };
            
            const newRows = [];

            for (let i = 1; i < csvLoadedUsers.length; i++) {
                const items = csvLoadedUsers[i];
                if(items.length === headersRow.length){
                    const userRole = rolesMapping[items[roleIndex]] || "";
                    if (!userRole) {
                        toast(`${t('Invalid role')}: ${items[roleIndex]}`, {
                            type: "error"
                        });
                        continue;
                    }

                    newRows.push({
                        first_name: items[firstNameIndex] || "",
                        last_name: items[lastNameIndex] || "",
                        email: items[emailIndex] || "",
                        position: items[companyPositionIndex] || "",
                        role: userRole,
                        salutation: items[salutationIndex] || "",
                        department: items[departmentIndex] || "",
                        phone_number: items[phoneNumberIndex] || "",
                        mobile_number: items[mobileNumberIndex] || "",
                    });
                }
            }

            if (newRows.length > 0) {
                setItems(newRows);
            }
        }
    }, [
        location.state
    ]);

    return (
        <Card>
            <CardBody>
                <AvForm
                    className="needs-validation"
                    onValidSubmit={handleSubmitMutation.mutate}
                >
                    <Row>
                        <Col sm="12">
                            <div className="user-management">
                                {items?.map((user, index) => {
                                    return (
                                        <Card key={index} className="mb-3 card-borderd">
                                            <CardBody>
                                                <Row>
                                                    <Col md="2" sm="12" className="mb-4">
                                                        <Label htmlFor={`salutation[${index}]`}>{`${t(
                                                            "Salutation"
                                                        )}:`}</Label>
                                                        <AvField
                                                            type="select"
                                                            name={`salutation[${index}]`}
                                                            className="form-control"
                                                            id={`salutation[${index}]`}
                                                            value={user?.salutation || ""}
                                                            onChange={(e) =>
                                                                fieldValueChanged(
                                                                    index,
                                                                    "salutation",
                                                                    e.target.value
                                                                )
                                                            }
                                                            validate={{
                                                                required: {
                                                                    value: true,
                                                                    errorMessage: t("Please select a salutation"),
                                                                },
                                                            }}
                                                        >
                                                            <option value="" hidden>
                                                                {t("Select")}
                                                            </option>
                                                            <option value="Mr.">{t("Mr.")}</option>
                                                            <option value="Mrs.">{t("Mrs.")}</option>
                                                            <option value="Ms.">{t("Ms.")}</option>
                                                            <option value="Mx.">{t("Mx.")}</option>
                                                        </AvField>
                                                    </Col>
                                                    <Col md="5" sm="12" className="mb-4">
                                                        <Label htmlFor={`first_name[${index}]`}>{`${t(
                                                            "First Name"
                                                        )}:`}</Label>
                                                        <AvField
                                                            name={`first_name[${index}]`}
                                                            placeholder=""
                                                            type="text"
                                                            errorMessage={t("This field cannot be blank")}
                                                            className="form-control"
                                                            validate={{
                                                                required: { value: true },
                                                            }}
                                                            id={`first-name[${index}]`}
                                                            value={user?.first_name}
                                                            onChange={(e) =>
                                                                fieldValueChanged(
                                                                    index,
                                                                    "first_name",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </Col>
                                                    <Col md="5" sm="12" className="mb-4">
                                                        <Label htmlFor={`last-name[${index}]`}>{`${t(
                                                            "Last Name"
                                                        )}:`}</Label>
                                                        <AvField
                                                            name={`last_name[${index}]`}
                                                            placeholder=""
                                                            type="text"
                                                            className="form-control"
                                                            validate={{
                                                                required: { value: false },
                                                            }}
                                                            id={`last-name[${index}]`}
                                                            value={user?.last_name}
                                                            onChange={(e) =>
                                                                fieldValueChanged(
                                                                    index,
                                                                    "last_name",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </Col>
                                                    <Col md="3" sm="12" className="mb-4">
                                                        <Label
                                                            htmlFor={`email[${index}]`}
                                                        >
                                                            {`${t("Email")}:`}
                                                        </Label>

                                                        <ValidatableEmail
                                                            index={index}
                                                            name={`email[${index}]`}
                                                            value={user?.email}
                                                            onChange={(e) => {
                                                                fieldValueChanged(
                                                                    index,
                                                                    "email",
                                                                    e.target.value
                                                                )
                                                            }}
                                                        />
                                                    </Col>
                                                    <Col md="3" sm="12" className="mb-4">
                                                        <Label htmlFor={`position[${index}]`}>{`${t(
                                                            "Position"
                                                        )}:`}</Label>
                                                        <AvField
                                                            name={`position[${index}]`}
                                                            placeholder=""
                                                            type="text"
                                                            className="form-control"
                                                            id={`position[${index}]`}
                                                            value={user?.position}
                                                            onChange={(e) =>
                                                                fieldValueChanged(
                                                                    index,
                                                                    "position",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </Col>
                                                    <Col md="3" sm="12" className="mb-4">
                                                        <Label 
                                                            htmlFor={`department[${index}]`}>
                                                            {`${t("Division")}:`}
                                                        </Label>
                                                        <AvField
                                                            name={`department[${index}]`}
                                                            placeholder=""
                                                            type="text"
                                                            className="form-control"
                                                            id={`department[${index}]`}
                                                            value={user?.department}
                                                            validate={{
                                                                required: {
                                                                    value: true
                                                                }
                                                            }}
                                                            onChange={(e) =>
                                                                fieldValueChanged(
                                                                    index,
                                                                    "department",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </Col>
                                                    <Col md="3" sm="12" className="mb-4">
                                                        <Label htmlFor={`phone_number[${index}]`}>
                                                            {`${t("Office Number")} ${t("(Optional)")}:`}
                                                        </Label>
                                                        <div className="sip-phone-input">
                                                            <InputPhoneNumber
                                                                id={`phone_number[${index}]`}
                                                                name={`phone_number[${index}]`}
                                                                required={false}
                                                                style={{ width: "100%" }}
                                                                placeholder=""
                                                                errorMessage=""
                                                                value={user?.phone_number || ""}
                                                                onChange={(phone) =>
                                                                    fieldValueChanged(
                                                                        index,
                                                                        "phone_number",
                                                                        phone
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col md="3" sm="12" className="mb-4">
                                                        <Label htmlFor={`mobile_number[${index}]`}>
                                                            {`${t("Mobile Number")} ${t("(Optional)")}:`}
                                                        </Label>
                                                        <div className="sip-phone-input">
                                                            <InputPhoneNumber
                                                                id={`mobile_number[${index}]`}
                                                                name={`mobile_number[${index}]`}
                                                                required={false}
                                                                style={{ width: "100%" }}
                                                                placeholder=""
                                                                errorMessage=""
                                                                value={user?.mobile_number || ""}
                                                                onChange={(phone) =>
                                                                    fieldValueChanged(
                                                                        index,
                                                                        "mobile_number",
                                                                        phone
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col md="3" sm="12" className="mb-4">
                                                        <Label htmlFor={`role-select[${index}]`}>{`${t(
                                                            "Role"
                                                        )}:`}</Label>
                                                        <AvField
                                                            type="select"
                                                            name={`role[${index}]`}
                                                            className="form-control"
                                                            id={`role-select[${index}]`}
                                                            validate={{
                                                                required: {
                                                                    value: true,
                                                                    errorMessage: t("Please select a role"),
                                                                },
                                                            }}
                                                            value={user?.role || ""}
                                                            onChange={(e) => {
                                                                fieldValueChanged(
                                                                    index,
                                                                    "role",
                                                                    e.target.value
                                                                );
                                                            }}
                                                        >
                                                            <option value="" hidden>
                                                                {t("Select Role")}
                                                            </option>
                                                            {Object.entries(roles)
                                                                .filter(([key, value]) =>
                                                                    value.modules.includes(activeModule)
                                                                )
                                                                .map(([key, value]) => (
                                                                    <option key={key} value={key}>
                                                                        {t(value.label)}
                                                                    </option>
                                                                ))}
                                                        </AvField>
                                                    </Col>
                                                </Row>

                                                {/* {isFeatureEnabledForActiveModule(
                                                    "entity_management"
                                                ) && (
                                                        <EntitySelection
                                                            index={index}
                                                            user={user}
                                                            onChange={({ section, e }) => {
                                                                if (section === 'entity') {
                                                                    fieldValueChanged(
                                                                        index,
                                                                        "entity_id",
                                                                        e.value
                                                                    );
                                                                    fieldValueChanged(index, "institute_id", "");
                                                                    fieldValueChanged(index, "division_id", "");
                                                                }
                                                                else if (section === 'institute') {
                                                                    fieldValueChanged(
                                                                        index,
                                                                        "institute_id",
                                                                        e.value
                                                                    );
                                                                    fieldValueChanged(index, "division_id", "");
                                                                }
                                                                else if (section === 'division') {
                                                                    fieldValueChanged(
                                                                        index,
                                                                        "division_id",
                                                                        e.value
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    )} */}

                                                <div className="d-flex justify-content-between">
                                                    <Button
                                                        color="danger"
                                                        onClick={() => deleteUser(index)}
                                                    >
                                                        <i className="fa fa-trash"></i>{" "}
                                                        {t("Delete Contact")}
                                                    </Button>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    );
                                })}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12">
                            <Button color="secondary" type="button" onClick={addUser}>
                                {t("Add User")}
                            </Button>
                            <Button color="primary" type="submit" style={{ float: "right" }}>
                                {handleSubmitMutation.isLoading ? (
                                    <Spinner 
                                        className="me-2" 
                                        size="sm"
                                    />
                                ) : (
                                    t("Send Invite")
                                )}
                            </Button>
                        </Col>
                    </Row>
                </AvForm>
            </CardBody>
        </Card>
    );
};

const mapStatetoProps = (state) => {
    const { token, user } = state.Login;
    const { Organization, Modules } = state;
    return {
        token,
        user,
        Organization,
        modules: Modules,
    };
};

export default withNamespaces()(
    withRouter(connect(mapStatetoProps)(memo(AddNewUser)))
);
